/**
 * Unified GARCH Parameter Estimation API
 * Tries MLE first, falls back to Method of Moments if needed
 */

import { GARCHMethodOfMomentsEstimator, type GARCHParameters } from './GARCHMethodOfMoments';
import { GARCHMaximumLikelihoodEstimator } from './GARCHMaximumLikelihood';

export type { GARCHParameters } from './GARCHMethodOfMoments';

export class GARCHParameterEstimator {
  /**
   * Estimate GARCH parameters with automatic fallback
   */
  estimateParameters(
    returns: number[],
    options: {
      method?: 'auto' | 'mle' | 'method-of-moments';
      initialGuess?: { omega: number; alpha: number; beta: number };
    } = {}
  ): GARCHParameters {
    const method = options.method || 'auto';
    
    if (method === 'method-of-moments') {
      return this.estimateViaMethodOfMoments(returns);
    }
    
    if (method === 'mle') {
      return this.estimateViaMaximumLikelihood(returns);
    }
    
    // Auto mode: Try MLE, fallback to Method of Moments
    try {
      const mleResult = this.estimateViaMaximumLikelihood(returns);
      
      // Check if MLE converged with reasonable confidence
      if (mleResult.confidence > 50) {
        return mleResult;
      }
      
      // MLE failed or low confidence, fallback
      console.warn('MLE failed to converge or low confidence, falling back to Method of Moments');
      return this.estimateViaMethodOfMoments(returns);
      
    } catch (error) {
      console.error('MLE estimation error, falling back to Method of Moments:', error);
      return this.estimateViaMethodOfMoments(returns);
    }
  }
  
  private estimateViaMaximumLikelihood(returns: number[]): GARCHParameters {
    const estimator = new GARCHMaximumLikelihoodEstimator();
    return estimator.estimateParameters(returns);
  }
  
  private estimateViaMethodOfMoments(returns: number[]): GARCHParameters {
    const estimator = new GARCHMethodOfMomentsEstimator();
    return estimator.estimateParameters(returns);
  }
}
